import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, Col, Row, Button, Spin, message } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  CameraOutlined,
  DownloadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./analytics.css";

const COLORS = ["#52c41a", "#f5222d"];

const EventAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  const icons = [
    <CalendarOutlined />,
    <UserOutlined />,
    <PictureOutlined />,
    <CheckCircleOutlined />,
    <CameraOutlined />,
  ];

  const descriptions = [
    "Events created on platform",
    "Registered users (all roles)",
    "Total uploaded photos",
    "Average face match success",
    "Photographers who contributed",
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("http://localhost:5000/api/analytics");
        setData(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching analytics:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const exportToPDF = () => {
    if (!chartRef.current) return;
    html2canvas(chartRef.current).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("event_analytics.pdf");
    });
  };

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "analytics_data.json";
    a.click();
    URL.revokeObjectURL(url);
    message.success("Analytics data downloaded!");
  };

  if (loading || !data) {
    return <Spin size="large" style={{ margin: "auto", display: "block", marginTop: 100 }} />;
  }

  const summary = [
    { title: "Total Events", value: data.summary.totalEvents },
    { title: "Total Users", value: data.summary.totalUsers },
    { title: "Total Photos", value: data.summary.totalPhotos },
    { title: "Match Accuracy", value: `${data.summary.matchAccuracy}%` },
    { title: "Photographers", value: data.summary.photographers },
  ];

  return (
    <div className="analytics-container">
      <h2 className="title">📊 Event Analytics Dashboard</h2>

      <Row gutter={[16, 16]}>
        {summary.map((card, index) => (
          <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
            <Card className="summary-card" bordered>
              <div className="card-icon">{icons[index]}</div>
              <div>
                <p className="card-title">{card.title}</p>
                <p className="card-value">{card.value}</p>
                <p className="card-desc" style={{ fontSize: 12, color: "#888" }}>
                  {descriptions[index]}
                </p>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div ref={chartRef} className="chart-section">

        {/* 📈 Events Trend */}
        <Card title="📈 Events Trend (Monthly)" className="chart-card">
          <p className="chart-note">Monthly count of events created on the platform.</p>
          {data.eventTrend.length === 0 ? (
            <p>No data to display</p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.eventTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" label={{ value: "Month", position: "insideBottom", offset: -5 }} />
                <YAxis label={{ value: "Events", angle: -90, position: "insideLeft" }} />
                <Tooltip />
                <Line type="monotone" dataKey="events" stroke="#1890ff" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* 📷 Monthly Uploads */}
        <Card title="📷 Monthly Uploads (by Photo Date)" className="chart-card">
          <p className="chart-note">Photos uploaded by photographers each month.</p>
          {data.photoUploads.length === 0 ? (
            <p>No data to display</p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.photoUploads}>
                <defs>
                  <linearGradient id="colorPhotos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ffc53d" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ffc53d" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" label={{ value: "Month", position: "insideBottom", offset: -5 }} />
                <YAxis label={{ value: "Photos", angle: -90, position: "insideLeft" }} />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip />
                <Area type="monotone" dataKey="photos" stroke="#ffc53d" fill="url(#colorPhotos)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* 📍 Match % Pie Chart */}
        <Card title="📍 Photo Match Accuracy" className="chart-card">
          <p className="chart-note">Distribution of matched and unmatched faces across all events.</p>
          {Array.isArray(data.pieChart) && data.pieChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data.pieChart} cx="50%" cy="50%" outerRadius={80} dataKey="value" label>
                  {data.pieChart.map((entry, index) => (
                    <Cell key={`match-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p>No match accuracy data available</p>
          )}
          <p style={{ fontSize: 12, textAlign: "center", marginTop: 5 }}>
            ✅ Matched Faces (Green), ❌ Unmatched Faces (Red)
          </p>
        </Card>
      </div>

      {/* Export Buttons */}
      <div className="download-buttons" style={{ marginTop: 24 }}>
        <Button type="primary" icon={<FilePdfOutlined />} onClick={exportToPDF}>
          Export Report
        </Button>
        <Button icon={<DownloadOutlined />} style={{ marginLeft: "10px" }} onClick={downloadJSON}>
          Download Data
        </Button>
      </div>
    </div>
  );
};

export default EventAnalytics;
